import { skipToken } from "@reduxjs/toolkit/query"
import { useMemo } from "react"

import { useGetEventsMergedDataQuery } from "../api/alaskaApi"
import { useGetEventMetadataQuery } from "../api/reigniteApi"

import { useFirebaseApiSource } from "./useFirebaseApiSource"

type EventsMergedApiData = {
  event: {
    athlinksId: number
    city: string | null
    countryId: string | null
    ctliveId: number | null
    isPhotoEnabled: boolean
    isVideoEnabled: boolean
    latitude: number | null
    logoUrl: string | null
    longitude: number | null
    masterId: number
    name: string
    photoPrice: number | null
    photoServiceName: string | null
    photoServiceType: string | null
    photoServiceUrl: string | null
    region: string | null
    regionId: string | null
    startEpoch: number
    timeZone: string | null
    wantsPhotoPurchasing: boolean
  }
}

const useEventsMerged = (masterId: number | null, eventId: number | null) => {
  const { apiSource, isFetchingFirebase, validEventId } = useFirebaseApiSource(masterId, eventId)

  const isReignite = apiSource === 'reignite'
  const isAlaska = apiSource === 'alaska'

  const { data: alaskaData, isLoading: isAlaskaLoading, error: alaskaError } = useGetEventsMergedDataQuery(validEventId ?? skipToken, { skip: !isAlaska })
  const { data: reigniteData, isLoading: isReigniteLoading, error: reigniteError } = useGetEventMetadataQuery(validEventId ?? skipToken, { skip: !isReignite })

  const isLoading = isFetchingFirebase || isAlaskaLoading || isReigniteLoading

  const mappedAlaskaData = useMemo<EventsMergedApiData | null>(() => {
    if (!isAlaska || !alaskaData) {
      return null
    }

    const { location, ...rest } = alaskaData.result

    const mappedData: EventsMergedApiData = {
      event: {
        ...rest,
        city: location.city,
        countryId: location.countryId,
        ctliveId: Number(rest.affiliateRaceId) ?? null,
        isPhotoEnabled: Boolean(rest.isPhotoEnabled),
        isVideoEnabled: Boolean(rest.isVideoEnabled),
        latitude: location.latitude || null,
        logoUrl: rest.imageUrl || null,
        longitude: location.longitude || null,
        masterId: rest.athlinksMasterId,
        name: rest.name,
        photoPrice: rest.photoPrice ?? null,
        photoServiceName: rest.photoServiceName ?? null,
        photoServiceType: rest.photoServiceType ?? null,
        photoServiceUrl: rest.photoServiceUrl ?? null,
        region: location.regionName,
        regionId: location.regionId || null,
        startEpoch: (new Date(rest.startTime).getTime() / 1000),
        timeZone: location.timeZone,
        wantsPhotoPurchasing: Boolean(rest.wantsPhotoPurchasing),
      }
    }

    return mappedData
  }, [alaskaData, isAlaska])

  const mappedReigniteData = useMemo<EventsMergedApiData | null>(() => {
    if (!isReignite || !reigniteData) {
      return null
    }

    const { location, media, ...rest } = reigniteData
    const { address } = location || {}

    const mappedData: EventsMergedApiData = {
      event: {
        athlinksId: rest.id,
        city: address?.city || null,
        countryId: address?.countryId || null,
        ctliveId: Number(rest.azpEventId) ?? null,
        isPhotoEnabled: !!media?.isPhotoEnabled,
        isVideoEnabled: !!media?.isVideoEnabled,
        latitude: location?.latitude || null,
        logoUrl: rest.logoUrl || null,
        longitude: location?.longitude || null,
        masterId: rest.masterId,
        name: rest.name,
        photoPrice: media?.photoPrice ?? null,
        photoServiceName: media?.photoServiceName || null,
        photoServiceType: media?.photoServiceType || null,
        photoServiceUrl: media?.photoServiceUrl || null,
        region: address?.region || null,
        regionId: address?.regionId || null,
        startEpoch: rest.start.epoch / 1000,
        timeZone: location?.timeZone || null,
        wantsPhotoPurchasing: !!media?.wantsPhotoPurchasing,
      }
    }

    return mappedData
  }, [isReignite, reigniteData])

  const response = useMemo<EventsMergedApiData | null>(() => {
    if (isReignite) {
      return mappedReigniteData
    }
    if (isAlaska) {
      return mappedAlaskaData
    }
    return null
  }, [isAlaska, isReignite, mappedAlaskaData, mappedReigniteData])

  const error = useMemo(() => {
    if (alaskaError) {
      return   }
    if (reigniteError) {
      return reigniteError
    }
    return null
  }, [alaskaError, reigniteError])

  return {
    eventsMerged: response,
    isEventsMergedLoading: isLoading,
    eventsMergedError: error,
  }
}

export { useEventsMerged }

export type { EventsMergedApiData }
